import { SearchInput } from "@/components/InputFields"
import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import { useCustomerSupportOverviewQuery } from "@/data/queries"
import {
  buildPatientWithPrescriptionData,
  buildShippingDeviceOrder,
  CustomerSupportDeviceOrder,
  CustomerSupportOverview,
  CustomerSupportPatient,
  CustomerSupportPatientWithPrescriptionData,
  CustomerSupportPrescription,
  CustomerSupportTroubleshootingItem,
  JourneyStatus,
} from "@/model/CustomerSupport"
import cn from "@/utils/cn"
import { ColumnDef } from "@tanstack/react-table"
import { ChangeEvent, useMemo, useState } from "react"
import {
  csFlagsColumns,
  enrollmentColumns,
  orderDevicesColumns,
  shippingColumns,
  systemTroubleshootingColumns,
} from "./columnDefinitions"
import TabBadge from "./components/TabBadge"
import CustomerSupportTable from "./CustomerSupportTable"

type TabConfig = {
  value: JourneyStatus
  label: string
}

const tabs: TabConfig[] = [
  { value: "cs-flags", label: "CS Flags" },
  { value: "enrollment", label: "Enrollment" },
  { value: "order-devices", label: "Order Devices" },
  { value: "shipping", label: "Shipping" },
  { value: "troubleshooting", label: "System Troubleshooting" },
]

const filterPrescription = (
  prescription: { patient: CustomerSupportPatient; organizationName: string },
  searchQuery: string
): boolean => {
  const searchQueryLowerCase = searchQuery.toLowerCase()
  const searchStringLowerCase = [
    prescription.patient.firstName,
    prescription.patient.lastName,
    prescription.organizationName,
  ]
    .join(" ")
    .toLowerCase()
  return searchStringLowerCase.includes(searchQueryLowerCase)
}

const filterDeviceOrder = (
  deviceOrder: CustomerSupportDeviceOrder,
  searchQuery: string
): boolean => {
  const searchQueryLowerCase = searchQuery.toLowerCase()

  const searchStringLowerCase = [
    deviceOrder.patient.firstName,
    deviceOrder.patient.lastName,
    deviceOrder.organizationName,
  ]
    .join(" ")
    .toLowerCase()

  return searchStringLowerCase.includes(searchQueryLowerCase)
}

const CustomerSupportPage = () => {
  const [activeTab, setActiveTab] = useState<JourneyStatus>("cs-flags")
  const [searchQuery, setSearchQuery] = useState("")

  const {
    data: rawCustomerSupportData,
    isPending: isLoading,
    isError,
  } = useCustomerSupportOverviewQuery()

  const customerSupportData = useMemo<
    CustomerSupportOverview | undefined
  >(() => {
    if (!rawCustomerSupportData || searchQuery.length === 0) {
      return rawCustomerSupportData
    }
    return {
      flaggedPrescriptions: rawCustomerSupportData.flaggedPrescriptions.filter(
        (p) => filterPrescription(p, searchQuery)
      ),
      enrolledPrescriptions:
        rawCustomerSupportData.enrolledPrescriptions.filter((p) =>
          filterPrescription(p, searchQuery)
        ),
      waitingDeviceOrderPrescriptions:
        rawCustomerSupportData.waitingDeviceOrderPrescriptions.filter((p) =>
          filterPrescription(p, searchQuery)
        ),
      shippingDeviceOrders: rawCustomerSupportData.shippingDeviceOrders.filter(
        (p) => filterDeviceOrder(p, searchQuery)
      ),
      troubleshootingItems: rawCustomerSupportData.troubleshootingItems.filter(
        (p) => filterPrescription(p, searchQuery)
      ),
    }
  }, [rawCustomerSupportData, searchQuery])

  // TODO: Add search functionality
  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  const getTabCount = (tab: JourneyStatus): number => {
    if (!customerSupportData) {
      return 0
    }
    if (tab === "cs-flags") {
      return buildPatientWithPrescriptionData(
        customerSupportData.flaggedPrescriptions
      ).length
    }
    if (tab === "enrollment") {
      return customerSupportData.enrolledPrescriptions.length
    }
    if (tab === "order-devices") {
      return customerSupportData.waitingDeviceOrderPrescriptions.length
    }
    if (tab === "shipping") {
      return buildShippingDeviceOrder(customerSupportData.shippingDeviceOrders)
        .length
    }
    if (tab === "troubleshooting") {
      return customerSupportData.troubleshootingItems.length
    }
    return 0
  }

  const currentTabData = useMemo(() => {
    if (!customerSupportData) {
      return []
    }
    if (activeTab === "cs-flags") {
      return buildPatientWithPrescriptionData(
        customerSupportData.flaggedPrescriptions.sort((a, b) =>
          a.type.localeCompare(b.type)
        )
      )
    }
    if (activeTab === "enrollment") {
      return customerSupportData.enrolledPrescriptions
    }
    if (activeTab === "order-devices") {
      return customerSupportData.waitingDeviceOrderPrescriptions
    }
    if (activeTab === "shipping") {
      return buildShippingDeviceOrder(customerSupportData.shippingDeviceOrders)
    }
    if (activeTab === "troubleshooting") {
      return customerSupportData.troubleshootingItems
    }
    return []
  }, [customerSupportData, activeTab])

  const tabColumns = useMemo(() => {
    switch (activeTab) {
      case "cs-flags":
        return csFlagsColumns
      case "enrollment":
        return enrollmentColumns
      case "order-devices":
        return orderDevicesColumns
      case "shipping":
        return shippingColumns
      case "troubleshooting":
        return systemTroubleshootingColumns
      default:
        return []
    }
  }, [activeTab])

  return (
    <Page>
      <div className="flex flex-col">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-4xl font-extrabold">Customer Support</h1>
          <div>
            <SearchInput
              placeholder="Search"
              className="w-[340px]"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
        </div>

        <div className="mb-6 flex items-center gap-2 border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={cn(
                "cursor-pointer flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors",
                activeTab === tab.value
                  ? "border-primary text-primary"
                  : "border-transparent text-primary/60 hover:text-primary/80"
              )}
            >
              {tab.label}
              <TabBadge
                count={getTabCount(tab.value)}
                active={activeTab === tab.value}
              />
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="mt-6 flex items-center justify-center">
            <Spinner className="me-2" />
            Loading customer support data...
          </div>
        ) : isError ? (
          <div className="p-4 text-center italic text-red-600">
            Failed to load customer support data. Please try again later.
          </div>
        ) : (
          <CustomerSupportTable
            data={currentTabData}
            columns={
              tabColumns as ColumnDef<
                | CustomerSupportPrescription
                | CustomerSupportPatientWithPrescriptionData
                | CustomerSupportDeviceOrder
                | CustomerSupportTroubleshootingItem
              >[]
            }
          />
        )}
      </div>
    </Page>
  )
}

export default CustomerSupportPage
